import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, ListChecks, Calculator, Bell,
  Users, LogOut, ShieldCheck, Menu, X, Radio, Activity, Settings as SettingsIcon, UserCog, Search, Truck, MessageCircle, Gauge, Inbox, Mail
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import useNotificationStream from "@/lib/useNotificationStream";
import { SupportChatWidget } from "@/components/SupportChatWidget";

const clientNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", group: "main" },
  { to: "/drivers", label: "Drivers", icon: UserCog, testid: "nav-drivers", group: "operations" },
  { to: "/vehicles", label: "Trucks", icon: Truck, testid: "nav-vehicles", group: "operations" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents", group: "operations" },
  { to: "/filings", label: "Filings", icon: ListChecks, testid: "nav-filings", group: "compliance" },
  { to: "/safety", label: "Safety & Tickets", icon: Activity, testid: "nav-safety", group: "compliance" },
  { to: "/csa-score", label: "CSA Score", icon: Gauge, testid: "nav-csa", group: "compliance" },
  { to: "/ifta", label: "IFTA Calculator", icon: Calculator, testid: "nav-ifta", group: "compliance" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", group: "account" },
];
const CLIENT_GROUP_LABELS = {
  main: "",
  operations: "OPERATIONS",
  compliance: "COMPLIANCE",
  account: "ACCOUNT",
};
// Admin (compliance head) — no money pages
const adminNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
  { to: "/admin/clients", label: "Clients", icon: Users, testid: "nav-admin-clients" },
  { to: "/admin/leads", label: "Leads Inbox", icon: Inbox, testid: "nav-admin-leads" },
  { to: "/admin/safety", label: "Safety", icon: Activity, testid: "nav-admin-safety" },
  { to: "/admin/csa", label: "CSA Snapshots", icon: Gauge, testid: "nav-admin-csa" },
  { to: "/admin/carrier-lookup", label: "Carrier Lookup", icon: Search, testid: "nav-admin-carrier-lookup" },
  { to: "/admin/support", label: "Support Inbox", icon: MessageCircle, testid: "nav-admin-support" },
  { to: "/admin/emails", label: "Alerts & Emails", icon: Mail, testid: "nav-admin-emails" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
];
// Super Admin (CEO)
const superAdminNav = [
  { to: "/super-admin", label: "Overview", icon: LayoutDashboard, testid: "nav-superadmin-dashboard" },
  { to: "/super-admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-superadmin-settings" },
  { to: "/admin", label: "Admin Console", icon: ShieldCheck, testid: "nav-superadmin-admin" },
  { to: "/admin/clients", label: "Clients", icon: Users, testid: "nav-superadmin-clients" },
  { to: "/admin/leads", label: "Leads Inbox", icon: Inbox, testid: "nav-superadmin-leads" },
  { to: "/admin/csa", label: "CSA Snapshots", icon: Gauge, testid: "nav-superadmin-csa" },
  { to: "/admin/carrier-lookup", label: "Carrier Lookup", icon: Search, testid: "nav-superadmin-carrier-lookup" },
  { to: "/admin/support", label: "Support Inbox", icon: MessageCircle, testid: "nav-superadmin-support" },
  { to: "/admin/emails", label: "Alerts & Emails", icon: Mail, testid: "nav-superadmin-emails" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!user || !user.id) return;
    let t;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.filter((n) => !n.read).length);
      } catch {}
    };
    load();
    t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  useNotificationStream(!!user?.id, (payload) => {
    if (payload?.type === "connected") setLive(true);
    if (payload?.type === "notification") setUnread((c) => c + 1);
    if (payload?.type === "support_message") {
      window.dispatchEvent(new CustomEvent("support-message", { detail: payload.data }));
    }
  });

  if (!user) return null;
  const items = user.role === "super_admin" ? superAdminNav
    : user.role === "admin" ? adminNav
    : clientNav;
  const consoleLabel = user.role === "super_admin" ? "CEO CONSOLE"
    : user.role === "admin" ? "ADMIN CONSOLE"
    : "DRIVER PORTAL";

  return (
    <div className="min-h-screen flex bg-white text-slate-900">
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-slate-50 border-r border-slate-200 flex flex-col transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-center">
          <img src="/logo.png" alt="Crystal Logistics Services" className="h-20 w-auto" data-testid="brand-logo" />
        </div>

        <nav className="px-3 py-4 flex-1 overflow-y-auto">
          <div className="hs-overline px-2 pb-2">{consoleLabel}</div>
          {items.map((it, idx) => {
            const Icon = it.icon;
            const groupLabel = user.role === "client" ? CLIENT_GROUP_LABELS[it.group] : "";
            const prevGroup = idx > 0 && user.role === "client" ? items[idx - 1].group : null;
            const showGroupHeader = user.role === "client" && groupLabel && it.group !== prevGroup;
            return (
              <div key={it.to}>
                {showGroupHeader && (
                  <div className="hs-overline text-[9px] text-slate-400 px-3 pt-4 pb-1 select-none">
                    {groupLabel}
                  </div>
                )}
                <NavLink
                  to={it.to}
                  end={it.to === "/admin" || it.to === "/dashboard" || it.to === "/super-admin"}
                  onClick={() => setOpen(false)}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 mb-1 text-sm border-l-2 min-h-[48px] transition-colors ${
                      isActive
                        ? "bg-blue-50 border-blue-700 text-blue-700 font-semibold"
                        : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                    }`
                  }
                >
                  <Icon size={16} />
                  <span className="flex-1">{it.label}</span>
                  {it.to === "/notifications" && unread > 0 && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-blue-700 text-white font-bold" data-testid="nav-unread-count">
                      {unread}
                    </span>
                  )}
                </NavLink>
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-2 pb-2">
            <div className="text-[10px] hs-overline">SIGNED IN</div>
            <div className="text-sm font-medium truncate" data-testid="signed-in-name">{user.name}</div>
            <div className="text-xs text-slate-500 truncate" data-testid="signed-in-email">{user.email}</div>
            {user.role === "super_admin" && (
              <div className="mt-2"><span className="hs-tag hs-tag-success">CEO · SUPER ADMIN</span></div>
            )}
            {user.role === "client" && (
              <div className="mt-2"><span className="hs-tag hs-tag-info">MANAGED CLIENT</span></div>
            )}
          </div>
          <button
            data-testid="logout-button"
            className="hs-btn-ghost w-full flex items-center gap-2 text-sm"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <LogOut size={14} /> SIGN OUT
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 bg-white">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden hs-btn-ghost p-2"
            onClick={() => setOpen(true)}
          >
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-blue-700" />
            <span className="hs-overline">SECURE SESSION</span>
            <span className="hs-overline text-blue-700 hidden sm:inline">· DOT-COMPLIANT</span>
            {live && (
              <span className="hs-overline hidden md:inline-flex items-center gap-1 text-emerald-600 ml-2" data-testid="live-indicator">
                <Radio size={10} className="animate-pulse" /> LIVE
              </span>
            )}
          </div>
          <Link to={user.role === "super_admin" ? "/super-admin" : user.role === "admin" ? "/admin" : "/dashboard"} className="hs-overline text-slate-500 hover:text-slate-900" data-testid="header-home-link">
            STATUS: ONLINE
          </Link>
        </header>
        {open && (
          <div className="lg:hidden fixed inset-0 bg-black/70 z-30" onClick={() => setOpen(false)}>
            <button className="absolute top-3 right-3 text-slate-900" onClick={() => setOpen(false)}><X /></button>
          </div>
        )}
        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
        {user.role === "client" && <SupportChatWidget />}
      </div>
    </div>
  );
}
